/**
 * ExplainEngine – translates a UIContext into a human-readable explanation
 * of the current screen. Used by the "Explain this screen" panel.
 *
 * Pure function: given the same context, returns the same explanation.
 * No side effects, no API calls.
 */

import type { UIContext } from './UIContextExtractor';
import { findKpi } from './UIContextExtractor';

export interface ExplainHighlight {
  text: string;
  /** Optional CSS selector to scroll-and-highlight when user clicks the bullet. */
  target?: string;
}

export interface Explanation {
  title: string;
  description: string;
  highlights: ExplainHighlight[];
  nextStep: string;
}

const fmt = (v: string | number | undefined): string => {
  if (v === undefined || v === null || v === '') return '—';
  return String(v);
};

export function generateExplanation(ctx: UIContext): Explanation {
  switch (ctx.pageType) {
    case 'Dashboard': {
      const patients = findKpi(ctx, 'patient');
      const pending = findKpi(ctx, 'pending') ?? findKpi(ctx, 'approval');
      const active = findKpi(ctx, 'active');

      return {
        title: 'Program Dashboard',
        description: `This dashboard gives you a real-time overview of the program${
          patients ? ` — currently ${fmt(patients.value)} ${patients.label.toLowerCase()}` : ''
        }${pending ? ` and ${fmt(pending.value)} ${pending.label.toLowerCase()}` : ''}.`,
        highlights: [
          {
            text: 'KPIs at the top show real-time program performance.',
            target: '[data-demo$="kpi-strip"], [data-kpi-strip]',
          },
          {
            text: 'Tables and charts are generated from dynamic Form Builder data.',
            target: '[data-demo$="table"]',
          },
          { text: 'Click any KPI or row to drill into details.' },
          ...(active
            ? [{ text: `${fmt(active.value)} ${active.label.toLowerCase()} are currently being tracked.` }]
            : []),
        ],
        nextStep:
          pending && Number(pending.value) > 0
            ? 'Review your pending approvals next.'
            : 'Open a patient to start the care workflow.',
      };
    }

    case 'PatientList': {
      const total = ctx.tableSummary?.totalRecords ?? 0;
      const cols = ctx.tableSummary?.visibleColumns ?? [];
      return {
        title: 'Patient List',
        description: `This table contains ${total} patients. You can search, filter, sort, and click any row to open the full Patient 360 view.`,
        highlights: [
          {
            text: `Columns are dynamic from Form Builder${cols.length ? ` — currently ${cols.length} visible` : ''}.`,
            target: '[data-demo$="table"], table',
          },
          {
            text: 'Use the filter panel on the left to narrow down patients.',
            target: '[data-demo="filter-panel"], [role="search"]',
          },
          { text: 'Click any row to open the patient\'s full profile and case history.' },
          ...(ctx.tableSummary?.hasFilters
            ? [{ text: `Active filters: ${ctx.filters.join(', ')}` }]
            : []),
        ],
        nextStep: 'Open a patient and create or review a case.',
      };
    }

    case 'Patient360': {
      const name = ctx.entity?.name ?? 'this patient';
      const cases = ctx.entity?.cases;
      const approved = ctx.entity?.approvedAmount;

      return {
        title: `Patient Details — ${name}`,
        description: `This view shows the complete journey for ${name}, including demographics, cases, workflow, documents, call logs, and financial assistance.`,
        highlights: [
          ...(cases !== undefined
            ? [
                {
                  text: `${cases} ${cases === 1 ? 'active case' : 'active cases'} on file.`,
                  target: '[data-demo="case-history"], [data-section-name="cases"]',
                },
              ]
            : []),
          ...(approved
            ? [
                {
                  text: `Total approved assistance: ${fmt(approved)}.`,
                  target: '[data-section-name="financial"], [data-demo="financial-summary"]',
                },
              ]
            : []),
          {
            text: 'The timeline shows every activity in chronological order.',
            target: '[data-section-name="timeline"], [data-demo="timeline"]',
          },
          {
            text: 'Call logs capture all interactions with this patient.',
            target: '[data-demo="call-log-section"]',
          },
        ],
        nextStep:
          cases === 0
            ? 'Create the first assistance case for this patient.'
            : 'Review the active case or log a follow-up call.',
      };
    }

    case 'CaseDetail': {
      const status = ctx.entity?.status ?? 'open';
      return {
        title: 'Case Detail',
        description: `This case is currently ${status.toLowerCase()}. Below you can see enrollment data, the assistance plan, utilization ledger, and workflow history.`,
        highlights: [
          { text: 'Workflow history shows every step taken on this case.' },
          { text: 'Utilization ledger entries are immutable and audit-tracked.' },
          {
            text: 'Approve, reject, or send back actions are available based on your role.',
            target: '[data-demo="approve-reject-btns"]',
          },
        ],
        nextStep: 'Take a workflow action or review utilization details.',
      };
    }

    case 'Workflow': {
      const pending = ctx.workflow.pendingTasks;
      const sla = ctx.workflow.slaBreached;
      return {
        title: 'Workflow Tasks',
        description: `You have ${pending} pending ${pending === 1 ? 'task' : 'tasks'}${
          ctx.workflow.currentStep ? ` at step "${ctx.workflow.currentStep}"` : ''
        }.${sla > 0 ? ` ${sla} SLA breach${sla === 1 ? '' : 'es'} require attention.` : ''}`,
        highlights: [
          { text: 'Tasks are assigned based on your program role.' },
          { text: 'SLA timers and escalation rules apply to every task.' },
          { text: 'Approve, reject, or send back — every decision is audit-logged.' },
        ],
        nextStep:
          sla > 0
            ? 'Resolve the SLA-breached tasks first.'
            : pending > 0
              ? 'Approve or review your pending tasks.'
              : 'No pending work — explore other workspaces.',
      };
    }

    case 'AssistanceList':
      return {
        title: 'Assistance Cases',
        description: `This workspace lists all assistance cases${
          ctx.tableSummary ? ` (${ctx.tableSummary.totalRecords} total)` : ''
        }. Each case is governed by its program's financial assistance model.`,
        highlights: [
          { text: 'Filter by case status, plan, or workflow step on the left.' },
          { text: 'Click any case to view its full ledger and workflow history.' },
          { text: 'Utilization, rebate, and entitlement calculations are server-side only.' },
        ],
        nextStep: 'Open a case to review its plan or take a workflow action.',
      };

    case 'Reports':
      return {
        title: 'Reports',
        description: 'Reports are generated dynamically from form data and configurable formulas — no static templates.',
        highlights: [
          { text: 'Fields are controlled by Form Builder configuration.' },
          { text: 'Supports aggregation, grouping, and scheduled execution.' },
          { text: 'Click any report row to drill down into the underlying data.' },
        ],
        nextStep: 'Pick a report to drill into trends and totals.',
      };

    case 'FormBuilder':
      return {
        title: 'Form Builder',
        description: 'Design dynamic forms that drive entity workflows. Forms follow DRAFT → PUBLISHED → ARCHIVED versioning.',
        highlights: [
          { text: 'Drag fields onto the canvas; layout updates live.' },
          { text: 'Conditional logic and field-level access rules are configured per field.' },
          { text: 'Publish to make a form usable; published forms are immutable until cloned.' },
        ],
        nextStep: 'Add or edit fields, then publish a new version when ready.',
      };

    case 'HCPList':
      return {
        title: 'Healthcare Professionals',
        description: `Listing all HCPs in this program${
          ctx.tableSummary ? ` — ${ctx.tableSummary.totalRecords} total` : ''
        }.`,
        highlights: [
          { text: 'HCP records drive case routing and clinical sign-off.' },
          { text: 'Filter by hospital, specialty, or status.' },
          { text: 'Click an HCP to see their assigned patients and cases.' },
        ],
        nextStep: 'Add a new HCP or open one to review their assignments.',
      };

    case 'HospitalList':
      return {
        title: 'Hospitals',
        description: `Listing all hospitals participating in this program${
          ctx.tableSummary ? ` — ${ctx.tableSummary.totalRecords} total` : ''
        }.`,
        highlights: [
          { text: 'Hospitals are the parent entity for HCPs and patient referrals.' },
          { text: 'Each hospital can have its own contact and address records.' },
        ],
        nextStep: 'Open a hospital or register a new one.',
      };

    case 'Settings':
      return {
        title: 'Settings',
        description:
          'Application configuration. Changes here are audit-logged and may take effect immediately for all users.',
        highlights: [
          { text: 'Secrets are encrypted at rest and masked in the UI.' },
          { text: 'Only SuperAdmin can change global settings.' },
        ],
        nextStep: 'Save your changes — they will apply program-wide.',
      };

    default:
      return {
        title: 'Screen Overview',
        description:
          'This screen displays dynamic data based on the current program configuration. Tooling and content adapt to your role and workflow.',
        highlights: ctx.kpis.length
          ? ctx.kpis.slice(0, 3).map((k) => ({ text: `${k.label}: ${fmt(k.value)}` }))
          : [{ text: 'Use the navigation on the left to explore available areas.' }],
        nextStep: 'Explore the available actions on this page.',
      };
  }
}
